import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';

/* ─── Color tokens ─── */
const ACCENT   = { completed: '#34d399', failed: '#f87171', pending: '#fbbf24', searching: '#67e8f9', matching: '#047857' };
const STATUS_BG = { completed: 'rgba(52,211,153,0.08)', failed: 'rgba(248,113,113,0.08)', pending: 'rgba(251,191,36,0.08)', searching: 'rgba(103,232,249,0.08)', matching: 'rgba(192,132,252,0.08)' };

const STATUS_LABEL = { completed: 'Completed', failed: 'Failed', pending: 'Queued', searching: 'Searching', matching: 'Matching' };

/* ─── Animated counter ─── */
const CountUp = ({ end, duration = 600 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!end) { setVal(0); return; }
    let start = 0; const step = end / (duration / 16);
    const id = setInterval(() => { start += step; if (start >= end) { setVal(end); clearInterval(id); } else setVal(Math.floor(start)); }, 16);
    return () => clearInterval(id);
  }, [end, duration]);
  return <span>{val}</span>;
};

/* ─── Stat card ─── */
const StatCard = ({ label, value, color, icon }) => (
  <div data-testid={`stat-${label.toLowerCase()}`} style={{
    flex: '1 1 140px', background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(12px)',
    borderRadius: 14, padding: '20px 18px', border: `1px solid ${color}15`,
    transition: 'border-color 0.3s, box-shadow 0.3s', position: 'relative', overflow: 'hidden'
  }}>
    <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `${color}06`, filter: 'blur(20px)' }} />
    <div style={{ fontSize: '0.6rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: '2rem', fontWeight: 800, color, letterSpacing: '-0.03em', lineHeight: 1 }}><CountUp end={value} /></div>
    <div style={{ marginTop: 6, fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>{icon} tasks</div>
  </div>
);

/* ─── Time ago helper ─── */
const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/* ─── Duration helper ─── */
const duration = (start, end) => {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

/* ─── Task row ─── */
const TaskRow = ({ task, onDelete, onViewResults, expanded, onToggle }) => {
  const color = ACCENT[task.status] || '#94a3b8';
  const bg = STATUS_BG[task.status] || 'rgba(148,163,184,0.08)';
  const result = task.result || {};

  return (
    <div data-testid={`task-row-${task.task_id}`} style={{
      background: 'rgba(255,255,255,0.015)', borderRadius: 12,
      border: `1px solid rgba(255, 255, 255, 0.85)`,
      marginBottom: 8, overflow: 'hidden',
      transition: 'border-color 0.2s, background 0.2s'
    }}>
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
          cursor: 'pointer', transition: 'background 0.15s'
        }}
      >
        {/* Status dot */}
        <div style={{
          width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0,
          boxShadow: `0 0 8px ${color}60`,
          animation: ['pending','searching','matching'].includes(task.status) ? 'pulse 2s infinite' : 'none'
        }} />

        {/* Type + Query */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task.query || 'Unknown query'}
            </span>
            <span style={{ background: bg, color, padding: '1px 8px', borderRadius: 6, fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, flexShrink: 0 }}>
              {STATUS_LABEL[task.status] || task.status}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 3 }}>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}>{task.type?.replace('_', ' ')}</span>
            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>{timeAgo(task.created_at)}</span>
            {task.completed_at && <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>{duration(task.created_at, task.completed_at)}</span>}
          </div>
        </div>

        {/* Result summary */}
        {result.total !== undefined && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ color, fontSize: '1.1rem', fontWeight: 700 }}>{result.total}</div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem' }}>results</div>
          </div>
        )}

        {/* Chevron */}
        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.9rem', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>&#9660;</span>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{
          padding: '0 18px 16px', borderTop: '1px solid rgba(255, 255, 255, 0.85)',
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 12 }}>
            {result.categories_matched !== undefined && (
              <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ color: '#64748B', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 1 }}>Categories</div>
                <div style={{ color: '#047857', fontSize: '1rem', fontWeight: 700, marginTop: 2 }}>{result.categories_matched}</div>
              </div>
            )}
            {result.deep_search_stats?.queries_executed && (
              <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ color: '#64748B', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 1 }}>Queries Run</div>
                <div style={{ color: '#67e8f9', fontSize: '1rem', fontWeight: 700, marginTop: 2 }}>{result.deep_search_stats.queries_executed}</div>
              </div>
            )}
            {result.deep_search_stats?.total_results_found && (
              <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ color: '#64748B', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 1 }}>Scanned</div>
                <div style={{ color: '#047857', fontSize: '1rem', fontWeight: 700, marginTop: 2 }}>{result.deep_search_stats.total_results_found}</div>
              </div>
            )}
            {result.deep_search_stats?.rejected !== undefined && (
              <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ color: '#64748B', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 1 }}>Rejected</div>
                <div style={{ color: '#f87171', fontSize: '1rem', fontWeight: 700, marginTop: 2 }}>{result.deep_search_stats.rejected}</div>
              </div>
            )}
            {task.exact_match !== undefined && (
              <div style={{ background: 'rgba(255, 255, 255, 0.85)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ color: '#64748B', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 1 }}>Match Mode</div>
                <div style={{ color: task.exact_match ? '#FEF08A' : '#34d399', fontSize: '0.8rem', fontWeight: 700, marginTop: 2 }}>{task.exact_match ? 'Exact' : 'Flexible'}</div>
              </div>
            )}
          </div>

          {/* Preview results */}
          {result.results_preview && result.results_preview.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ color: '#64748B', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Preview</div>
              {result.results_preview.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" style={{
                  display: 'block', padding: '6px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.85)',
                  textDecoration: 'none', transition: 'padding-left 0.15s'
                }}>
                  <div style={{ color: '#1e293b', fontSize: '0.72rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || r.url}</div>
                  <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.url}</div>
                </a>
              ))}
            </div>
          )}

          {/* Error */}
          {task.error && (
            <div style={{ marginTop: 10, background: 'rgba(248,113,113,0.06)', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(248,113,113,0.1)' }}>
              <span style={{ color: '#f87171', fontSize: '0.7rem' }}>{task.error}</span>
            </div>
          )}

          {/* Category summary */}
          {result.category_summary && result.category_summary.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {result.category_summary.slice(0, 8).map((cat, i) => (
                <span key={i} style={{
                  background: 'rgba(192,132,252,0.08)', border: '1px solid rgba(192,132,252,0.12)',
                  padding: '2px 8px', borderRadius: 6, fontSize: '0.6rem', color: '#047857', fontWeight: 500
                }}>{cat.name} ({cat.result_count})</span>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            {task.status === 'completed' && result.batch_id && (
              <button onClick={() => onViewResults(task)} data-testid={`view-results-${task.task_id}`} style={{
                background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)',
                color: '#34d399', padding: '6px 14px', borderRadius: 8, fontSize: '0.68rem',
                fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s'
              }}>View Full Results</button>
            )}
            <button onClick={() => onDelete(task.task_id)} data-testid={`delete-task-${task.task_id}`} style={{
              background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.12)',
              color: '#f87171', padding: '6px 14px', borderRadius: 8, fontSize: '0.68rem',
              fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s'
            }}>Delete</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
};

/* ─── Main Page ─── */
const TaskHistoryPage = () => {
  const { user, token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, failed: 0, pending: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const pollRef = useRef(null);

  const fetchTasks = useCallback(() => {
    const xhr = new XMLHttpRequest();
    let url = `${API}/task-history?page=${page}&limit=15`;
    if (filter) url += `&status_filter=${filter}`;
    xhr.open('GET', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            setTasks(data.tasks || []);
            setStats(data.stats || { total: 0, completed: 0, failed: 0, pending: 0 });
            setTotalPages(data.pages || 0);
          } catch(e) {}
        }
        setLoading(false);
      }
    };
    xhr.send();
  }, [token, page, filter]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Auto-refresh when tasks are pending
  useEffect(() => {
    if (stats.pending > 0) {
      pollRef.current = setInterval(fetchTasks, 3000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [stats.pending, fetchTasks]);

  const deleteTask = (taskId) => {
    const xhr = new XMLHttpRequest();
    xhr.open('DELETE', `${API}/task-history/${taskId}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() { if (xhr.status === 200) fetchTasks(); };
    xhr.send();
  };

  const clearHistory = () => {
    const xhr = new XMLHttpRequest();
    xhr.open('DELETE', `${API}/task-history`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() { if (xhr.status === 200) fetchTasks(); };
    xhr.send();
  };

  const viewResults = (task) => {
    // Navigate to search page with batch filter
    window.location.hash = `#/search?batch=${task.result?.batch_id}`;
  };

  const filters = [
    { key: '', label: 'All' },
    { key: 'completed', label: 'Completed' },
    { key: 'failed', label: 'Failed' },
    { key: 'pending', label: 'Active' }
  ];

  if (!user) return <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>Please log in to view task history.</div>;

  return (
    <div data-testid="task-history-page" style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.03em', margin: 0 }}>Task History</h1>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', marginTop: 4, marginBottom: 0 }}>Track and manage your background processing tasks</p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total" value={stats.total} color="#94a3b8" icon="All" />
        <StatCard label="Completed" value={stats.completed} color="#34d399" icon="Done" />
        <StatCard label="Failed" value={stats.failed} color="#f87171" icon="Error" />
        <StatCard label="Active" value={stats.pending} color="#fbbf24" icon="Running" />
      </div>

      {/* Filters + Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); setPage(1); }}
              data-testid={`filter-${f.key || 'all'}`}
              style={{
                background: filter === f.key ? 'rgba(203, 213, 225, 0.8)' : 'transparent',
                border: `1px solid ${filter === f.key ? 'rgba(255,255,255,0.12)' : 'rgba(255, 255, 255, 0.85)'}`,
                color: filter === f.key ? 'rgba(255,255,255,0.85)' : '#64748B',
                padding: '5px 14px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s'
              }}
            >{f.label}</button>
          ))}
        </div>
        <button
          onClick={clearHistory}
          data-testid="clear-history-btn"
          style={{
            background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.12)',
            color: '#f87171', padding: '5px 14px', borderRadius: 8, fontSize: '0.68rem',
            fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
          }}
        >Clear Finished</button>
      </div>

      {/* Task list */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ width: 24, height: 24, border: '2px solid rgba(255,255,255,0.1)', borderTopColor: '#047857', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : tasks.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12, opacity: 0.15 }}>&#9881;</div>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem' }}>No tasks found</p>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem' }}>Run an Auto-Categorize search to see tasks here</p>
        </div>
      ) : (
        <div data-testid="task-list">
          {tasks.map(task => (
            <TaskRow
              key={task.task_id}
              task={task}
              expanded={expanded === task.task_id}
              onToggle={() => setExpanded(expanded === task.task_id ? null : task.task_id)}
              onDelete={deleteTask}
              onViewResults={viewResults}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 20 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="prev-page"
            style={{
              background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(255,255,255,0.06)',
              color: page <= 1 ? 'rgba(255,255,255,0.15)' : '#475569',
              padding: '5px 12px', borderRadius: 8, fontSize: '0.72rem', cursor: page <= 1 ? 'default' : 'pointer'
            }}
          >Prev</button>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', alignSelf: 'center' }}>{page} / {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            data-testid="next-page"
            style={{
              background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(255,255,255,0.06)',
              color: page >= totalPages ? 'rgba(255,255,255,0.15)' : '#475569',
              padding: '5px 12px', borderRadius: 8, fontSize: '0.72rem', cursor: page >= totalPages ? 'default' : 'pointer'
            }}
          >Next</button>
        </div>
      )}
    </div>
  );
};

export default TaskHistoryPage;
